import React, { useMemo, useRef, useState } from "react";
import "./QuotationPage.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { updateQuoteDetails } from "../../redux/slices/QuoteDetailsSlice";
import { useEffect } from "react";
import { genSharedQuotePayload } from "../utility/payloadBuilder";
import QuotationCard from "../components/QuotationCard";
import EditDetailForm from "../forms/EditDetailForm";
import Modal from "react-bootstrap/Modal";
import { updateTravellerDetails } from "../../redux/slices/TravellerDetailsSlice";
import { useSearchParams } from "react-router-dom";
import {
  CommaSeprator,
  getNormalizedQueryParams,
  formatSafeDate,
  formatToDDMMYYYY,
  getDobFromAge,
} from "../utility/utilityFunctions";
import { toast } from "react-toastify";
import API from "../../utils/API";
import editDetailsIMG from "../../Images/EditDetails.svg";
import { setEnquiryData } from "../../redux/slices/EnquirySlice";
import { updateMastersData } from "../../redux/slices/MastersDataSlice";

const QuotationPage = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  /* Modal visibility */
  const [showSumAssuredModal, setShowSumAssuredModal] = useState(false);
  const [showInsurerModal, setShowInsurerModal] = useState(false);
  const [draftInsurerIds, setDraftInsurerIds] = useState(new Set());
  const hasAppliedDefaultSumAssuredRef = useRef(false);
  const hasAppliedDefaultInsurerRef = useRef(false);

  const dispatch = useDispatch();
  const quoteDetails = useSelector((state) => state.QuoteDetails);
  const mastersData = useSelector((state) => state.MastersData);
  const enquiryState = useSelector((state) => state.Enquiry);
  const enquiryData = enquiryState?.EnquiryData || {};
  const relationList = Array.isArray(mastersData?.relationList)
    ? mastersData.relationList
    : [];
  const {
    DestinationList,
    StartDate,
    EndDate,
    Travelers,
    numberOfDays,
    MobileNo,
    tripType,
  } = enquiryData;

  const [searchParams] = useSearchParams();
  const params = getNormalizedQueryParams(searchParams);
  const [insurerList, setInsurerList] = useState([]);

  const isQuoteLoading = Boolean(quoteDetails?.quoteLoading);
  const travelerCount = Array.isArray(Travelers) ? Travelers.length : 0;
  const sortPremium = quoteDetails?.filters?.sortPremium || "";
  const currentFilters = quoteDetails?.filters || {};

  /* Sum Assured */
  const sumAssuredOptions = Array.isArray(mastersData?.sumAssuredList)
    ? mastersData.sumAssuredList.filter((r) => r?.PlanSuminsuredID)
    : [];
  const selectedSumAssuredObj = currentFilters?.sumAssuredObj || null;
  const getSumAssuredValue = (item) =>
    Number(item?.MaxValue ?? item?.MinValue ?? 0);

  /* Insurer filter — multi-select, Set of InsurerIDs; empty Set = All */
  const selectedInsurerIds =
    currentFilters?.selectedInsurerIds instanceof Set
      ? currentFilters.selectedInsurerIds
      : new Set();

  const allQuotes = Array.isArray(quoteDetails?.quotes)
    ? quoteDetails.quotes
    : [];
  const visibleQuotes =
    selectedInsurerIds.size === 0
      ? allQuotes
      : allQuotes.filter((row) =>
          selectedInsurerIds.has(
            Number(row?.InsurerID ?? row?.InsurerId ?? row?.insurerID ?? 0),
          ),
        );
  const planCount = visibleQuotes.length;

  const getInsurerName = (insurer) =>
    insurer?.ShortName || insurer?.InsurerName || insurer?.Name || "";

  const insurerDisplayLabel = (() => {
    if (selectedInsurerIds.size === 0) return "All Insurers";
    const names = insurerList
      .filter((i) => selectedInsurerIds.has(Number(i.InsurerID)))
      .map((i) => getInsurerName(i))
      .filter(Boolean);
    return names.join(", ");
  })();

  const insurerDisplaySub = (() => {
    if (selectedInsurerIds.size === 0) return "No filter applied";
    return `${selectedInsurerIds.size} insurer${selectedInsurerIds.size > 1 ? "s" : ""} selected`;
  })();

  const getPolicyType = (tripTypeValue, travellerCountValue) => {
    const normalizedTripType = String(tripTypeValue || "").toLowerCase();
    if (normalizedTripType.includes("student")) return "Student";
    return travellerCountValue === 1 ? "Individual" : "Family";
  };

  const quotationFetchKey = useMemo(() => {
    if (!insurerList.length) return "";
    if (!enquiryState?.EnquiryNo || !enquiryState?.LotNo) return "";

    const destinations = (enquiryData.DestinationList || [])
      .map((d) => d?.Id ?? d?.CountryName ?? "")
      .filter(Boolean)
      .sort();

    const travellers = (enquiryData.Travelers || []).map((t) => ({
      age: Number(t?.age ?? t?.Age ?? 0),
      relation: Number(t?.relation ?? t?.Relation ?? 0),
    }));

    const insurerIds = insurerList
      .map((i) => Number(i?.InsurerID) || 0)
      .filter(Boolean)
      .sort((a, b) => a - b);

    return JSON.stringify({
      enquiryNo: String(enquiryState.EnquiryNo || ""),
      lotNo: String(enquiryState.LotNo || ""),
      mobile: String(enquiryData.MobileNo || ""),
      start: String(enquiryData.StartDate || ""),
      end: String(enquiryData.EndDate || ""),
      tripType: String(enquiryData.tripType || ""),
      numberOfDays: Number(enquiryData.numberofDays || 0),
      policyType: String(
        enquiryData.policyType ||
          getPolicyType(
            enquiryData.tripType,
            (enquiryData.Travelers || []).length,
          ),
      ),
      destinations,
      travellers,
      insurerIds,
      sumAssured: Number(currentFilters?.sumAssured || 0),
    });
  }, [
    insurerList,
    enquiryState?.EnquiryNo,
    enquiryState?.LotNo,
    enquiryData.MobileNo,
    enquiryData.StartDate,
    enquiryData.EndDate,
    enquiryData.tripType,
    enquiryData.policyType,
    enquiryData.numberofDays,
    enquiryData.DestinationList,
    enquiryData.Travelers,
    currentFilters?.sumAssured,
  ]);

  /* ── Handlers ── */
  const handleCopy = () => {
    navigator.clipboard.writeText(enquiryState?.EnquiryNo || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (!sortOpen) return;
    const onDocDown = (e) => {
      if (!sortRef.current || sortRef.current.contains(e.target)) return;
      setSortOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [sortOpen]);

  useEffect(() => {
    if (!showInsurerModal) return;
    setDraftInsurerIds(new Set(selectedInsurerIds));
  }, [showInsurerModal, selectedInsurerIds]);

  const applySort = (next) => {
    dispatch(
      updateQuoteDetails({ filters: { ...currentFilters, sortPremium: next } }),
    );
    setSortOpen(false);
  };

  const applySumAssured = (item) => {
    const nextSumAssured = getSumAssuredValue(item);
    dispatch(
      updateQuoteDetails({
        filters: {
          ...currentFilters,
          sumAssured: nextSumAssured,
          sumAssuredObj: item || null,
        },
      }),
    );
    setShowSumAssuredModal(false);
  };

  const toggleDraftInsurer = (insurerId) => {
    const next = new Set(draftInsurerIds);
    if (insurerId === null) {
      /* "All" clicked → clear selection */
      next.clear();
    } else {
      next.has(insurerId) ? next.delete(insurerId) : next.add(insurerId);
    }
    setDraftInsurerIds(next);
  };

  const applyInsurerFilter = () => {
    dispatch(
      updateQuoteDetails({
        filters: { ...currentFilters, selectedInsurerIds: draftInsurerIds },
      }),
    );
    setShowInsurerModal(false);
  };

  /* ── Data fetching ── */
  useEffect(() => {
    if (params.enquiryno || params.shareid) getSharedQuotations();
  }, []);

  useEffect(() => {
    getInsurerMaster();
  }, []);
  useEffect(() => {
    getPlanSuminsuredMaster();
  }, []);
  useEffect(() => {
    getRelationListMaster();
  }, []);

  useEffect(() => {
    if (hasAppliedDefaultSumAssuredRef.current) return;
    if (selectedSumAssuredObj) {
      hasAppliedDefaultSumAssuredRef.current = true;
      return;
    }
    if (!sumAssuredOptions.length) return;

    const defaultSumAssured =
      sumAssuredOptions.find((item) => Number(item.PlanSuminsuredID) === 1) ||
      sumAssuredOptions.find(
        (item) =>
          String(item.Level || "").includes("1 - ₹2 Lakh") ||
          (Number(item.MinValue) === 100000 &&
            Number(item.MaxValue) === 200000),
      ) ||
      null;

    if (!defaultSumAssured) return;

    hasAppliedDefaultSumAssuredRef.current = true;
    dispatch(
      updateQuoteDetails({
        filters: {
          ...currentFilters,
          sumAssured: getSumAssuredValue(defaultSumAssured),
          sumAssuredObj: defaultSumAssured,
        },
      }),
    );
  }, [dispatch, currentFilters, selectedSumAssuredObj, sumAssuredOptions]);

  useEffect(() => {
    if (hasAppliedDefaultInsurerRef.current) return;
    if (selectedInsurerIds.size > 0) {
      hasAppliedDefaultInsurerRef.current = true;
      return;
    }
    if (!insurerList.length) return;

    const defaultInsurer =
      insurerList.find((ins) =>
        String(getInsurerName(ins)).toLowerCase().includes("indus"),
      ) || null;

    if (!defaultInsurer) return;

    hasAppliedDefaultInsurerRef.current = true;
    dispatch(
      updateQuoteDetails({
        filters: {
          ...currentFilters,
          selectedInsurerIds: new Set([Number(defaultInsurer.InsurerID)]),
        },
      }),
    );
  }, [dispatch, currentFilters, insurerList, selectedInsurerIds]);

  const getInsurerMaster = async () => {
    try {
      const model = { URL: "GetInsurers", PostString: JSON.stringify([]) };
      const Res = await API.CallRequestWithHeader(model);
      const nextInsurers = Res?.Response?.length > 0 ? Res.Response : [];
      setInsurerList(nextInsurers);
      dispatch(
        updateMastersData({
          insurersList: nextInsurers,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getPlanSuminsuredMaster = async () => {
    try {
      if (mastersData?.sumAssuredList?.length > 0) return;
      const model = {
        URL: "GetPlanSuminsuredList",
        PostString: JSON.stringify([]),
      };
      const Res = await API.CallRequestWithHeader(model);
      dispatch(
        updateMastersData({
          sumAssuredList: Res?.Response?.length > 0 ? Res.Response : [],
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };
  const getRelationListMaster = async () => {
    try {
      if (relationList.length > 0) return;
      const model = {
        URL: "GetRelationTypeList",
        PostString: JSON.stringify([]),
      };
      const Res = await API.CallRequestWithHeader(model);
      dispatch(
        updateMastersData({
          relationList: Res?.Response?.length > 0 ? Res.Response : [],
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getRelationName = (relationId) =>
    relationList.find((r) => Number(r?.RelationID) === Number(relationId))
      ?.RelationName || "Member";

  const getSharedQuotations = async () => {
    try {
      const req = genSharedQuotePayload(params);
      const model = {
        URL: "/api/Others/ShareTravelDataGet",
        PostString: JSON.stringify(req),
      };
      await axios.post("services/call", model);
    } catch (error) {
      toast.warn(error?.message);
    }
  };

  const buildQuotationPayload = (insurerId, enquiryDataOverride = null) => {
    const data = enquiryDataOverride || enquiryState?.EnquiryData || {};
    const days = Number(data.numberofDays || 0);
    const destinationList = data.DestinationList || [];
    const travellers = (data.Travelers || []).map((traveller) => ({
      ...traveller,
      dob:
        traveller?.dob ||
        traveller?.DOB ||
        getDobFromAge(traveller?.age ?? traveller?.Age),
    }));
    return {
      MobileNo: data.MobileNo || "",
      StartDate: formatToDDMMYYYY(data.StartDate || ""),
      EndDate: formatToDDMMYYYY(data.EndDate || ""),
      Travelers: travellers,
      numberofDays: days,
      Destination: destinationList.map((d) => d.CountryName),
      DestinationList: destinationList,
      enquiryNo: enquiryState?.EnquiryNo || "",
      policyType:
        data.policyType || getPolicyType(data.tripType, travellers.length),
      tripType:
        data.tripType === "Student" ? "Single" : data.tripType || "Single",
      tripDuration: String(Math.max(days, 0)),
      SumAssured: currentFilters?.sumAssured || 0,
      LotNo: enquiryState?.LotNo || "",
      InsurerID: insurerId,
    };
  };

  const buildQuotesFetchKey = (enquiryDataOverride = null) => {
    if (!insurerList.length) return "";
    const data = enquiryDataOverride || enquiryState?.EnquiryData || {};
    if (!data) return "";
    if (!enquiryState?.EnquiryNo || !enquiryState?.LotNo) return "";
    const destinations = (data.DestinationList || [])
      .map((d) => d?.Id ?? d?.CountryName ?? "")
      .filter(Boolean)
      .sort();
    const travellers = (data.Travelers || []).map((t) => ({
      age: Number(t?.age ?? t?.Age ?? 0),
      relation: Number(t?.relation ?? t?.Relation ?? 0),
    }));
    const insurerIds = insurerList
      .map((i) => Number(i?.InsurerID) || 0)
      .filter(Boolean)
      .sort((a, b) => a - b);
    return JSON.stringify({
      enquiryNo: String(enquiryState.EnquiryNo || ""),
      lotNo: String(enquiryState.LotNo || ""),
      mobile: String(data.MobileNo || ""),
      start: String(data.StartDate || ""),
      end: String(data.EndDate || ""),
      tripType: String(data.tripType || ""),
      numberOfDays: Number(data.numberofDays || 0),
      policyType: String(
        data.policyType ||
          getPolicyType(data.tripType, (data.Travelers || []).length),
      ),
      destinations,
      travellers,
      insurerIds,
      filters: quoteDetails?.filters
        ? {
            ...quoteDetails.filters,
            selectedInsurerIds: undefined, // Sets aren't serialisable
          }
        : {},
    });
  };

  const fetchAllQuotations = async ({
    force = false,
    enquiryDataOverride = null,
  } = {}) => {
    const key = buildQuotesFetchKey(enquiryDataOverride);
    if (!key) return;
    if (
      !force &&
      quoteDetails?.lastFetchKey === key &&
      Array.isArray(quoteDetails?.quotes) &&
      quoteDetails.quotes.length > 0 &&
      !quoteDetails.quoteLoading
    )
      return;

    try {
      dispatch(
        updateQuoteDetails({
          quotes: [],
          quoteLoading: true,
          lastFetchKey: key,
        }),
      );
      const requests = insurerList.map((insurer) =>
        API.CallRequestWithHeader({
          URL: "GetAllQuotation",
          PostString: JSON.stringify(
            buildQuotationPayload(insurer.InsurerID, enquiryDataOverride),
          ),
        }),
      );
      const responses = await Promise.all(requests);
      const mergedQuotes = responses
        .filter((res) => res?.StatusCode === 200 && res?.Response)
        .flatMap((res) => res.Response.planAndPremiumDetails || []);
      dispatch(
        updateQuoteDetails({
          quotes: mergedQuotes,
          quoteLoading: false,
          lastFetchKey: key,
        }),
      );
    } catch {
      dispatch(
        updateQuoteDetails({
          quotes: [],
          quoteLoading: false,
          lastFetchKey: "",
        }),
      );
    }
  };

  useEffect(() => {
    fetchAllQuotations();
  }, [quotationFetchKey]);

  const updateDetails = (data) => {
    const nextEnquiryData = {
      ...(enquiryState?.EnquiryData || {}),
      MobileNo: data.mobileNo,
      StartDate: data.startDate,
      EndDate: data.endDate,
      Travelers: data.travellers,
      numberofDays: Number(data.numberOfDays || 0),
      DestinationList: data.destination,
      Destination: (data.destination || []).map((d) => d.CountryName),
      tripType: data.tripType,
      policyType: getPolicyType(data.tripType, data.travellers?.length || 0),
      sponsorDetails: enquiryState?.EnquiryData?.sponsorDetails,
      studentDetails: enquiryState?.EnquiryData?.studentDetails,
    };
    dispatch(setEnquiryData(nextEnquiryData));
    dispatch(
      updateTravellerDetails({
        destination: data.destination,
        mobileNo: data.mobileNo,
        startDate: data.startDate,
        endDate: data.endDate,
        numberOfDays: data.numberOfDays,
        travellers: data.travellers,
        tripType: data.tripType,
        studentDetails: nextEnquiryData.studentDetails,
      }),
    );
  };

  /* ── Render ── */
  return (
    <div className="Quot_Background">
      <div className="container">
        <div className="qp-layout">
          {/* ───────────── LEFT ───────────── */}
          <div className="qp-left">
            {/* ── PLANS BAR ── */}
            <div className="qp-plans-bar">
              {/* 1. Plans count */}
              <div className="qp-bar-count">
                {isQuoteLoading ? (
                  <span className="qp-bar-count-loader" aria-label="Loading">
                    <span className="qp-bar-count-spinner" />
                  </span>
                ) : (
                  <span className="qp-bar-count-num">{planCount}</span>
                )}
                <span className="qp-bar-count-lbl">
                  {isQuoteLoading ? "Finding Plans" : "Plans Found"}
                </span>
              </div>

              {/* <div className="qp-bar-vd" /> */}

              {/* 2. Lead ID */}
              <div className="qp-bar-seg">
                <span className="qp-bar-seg-lbl">Lead ID</span>
                <div className="qp-lead-chip">
                  <span className="qp-lead-id">
                    {enquiryState?.EnquiryNo || "—"}
                  </span>
                  <button
                    className="qp-copy-btn"
                    onClick={handleCopy}
                    title="Copy"
                  >
                    {copied ? (
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        width="13"
                        height="13"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M3 8l3.5 3.5L13 4.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        width="13"
                        height="13"
                        stroke="currentColor"
                        strokeWidth="1.7"
                      >
                        <rect x="5" y="5" width="8" height="8" rx="2" />
                        <path
                          d="M3 11V3h8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="qp-bar-vd" />

              {/* 3. Sum Assured filter */}
              <div className="qp-bar-seg qp-bar-seg--grow">
                <span className="qp-bar-seg-lbl">Sum Assured</span>
                <button
                  type="button"
                  className="qp-filter-pill"
                  onClick={() =>
                    sumAssuredOptions.length > 0 && setShowSumAssuredModal(true)
                  }
                  disabled={sumAssuredOptions.length === 0}
                >
                  <div className="qp-filter-pill-left">
                    <span className="qp-filter-pill-val">
                      {selectedSumAssuredObj
                        ? selectedSumAssuredObj.Level
                        : "Recommended"}
                    </span>
                    {/* <span className="qp-filter-pill-sub">
                      {selectedSumAssuredObj
                        ? `Starts ₹${CommaSeprator(selectedSumAssuredObj.MinValue)}`
                        : "Default cover"}
                    </span> */}
                  </div>
                  <div className="qp-filter-pill-caret">
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      width="13"
                      height="13"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M4 6l4 4 4-4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>

              <div className="qp-bar-vd" />

              {/* 4. Insurer filter */}
              <div className="qp-bar-seg qp-bar-seg--grow">
                <span className="qp-bar-seg-lbl">Insurer</span>
                <button
                  type="button"
                  className="qp-filter-pill"
                  onClick={() => setShowInsurerModal(true)}
                >
                  <div className="qp-filter-pill-left">
                    <span className="qp-filter-pill-val">
                      {insurerDisplayLabel}
                    </span>
                    {/* <span className="qp-filter-pill-sub">
                      {insurerDisplaySub}
                    </span> */}
                  </div>
                  <div className="qp-filter-pill-caret">
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      width="13"
                      height="13"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M4 6l4 4 4-4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>

              {/* 5. Sort */}
              <div className="qp-sort-wrap" ref={sortRef}>
                {/* <span className="qp-bar-seg-lbl">Sort</span> */}
                <button
                  className="qp-sort-btn"
                  type="button"
                  onClick={() => setSortOpen((v) => !v)}
                  aria-expanded={sortOpen}
                >
                  <span className="qp-sort-lines">
                    <span />
                    <span />
                    <span />
                  </span>
                  Sort
                </button>

                {sortOpen && (
                  <div className="qp-sort-menu" role="menu">
                    <button
                      type="button"
                      className={`qp-sort-item${sortPremium === "high_to_low" ? " active" : ""}`}
                      onClick={() => applySort("high_to_low")}
                      role="menuitem"
                    >
                      Premium: High to Low
                    </button>
                    <button
                      type="button"
                      className={`qp-sort-item${sortPremium === "low_to_high" ? " active" : ""}`}
                      onClick={() => applySort("low_to_high")}
                      role="menuitem"
                    >
                      Premium: Low to High
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* ── END PLANS BAR ── */}

            <QuotationCard />
          </div>

          {/* ───────────── RIGHT SIDEBAR ───────────── */}
          <div className="qp-right">
            <div className="qp-sidebar">
              <div className="qp-sb-head">
                <div className="qp-sb-badge">
                  <span className="qp-sb-dot" />
                  <span className="qp-sb-badge-text">{tripType || "Trip"}</span>
                </div>
                <button
                  className="qp-edit-btn"
                  onClick={() => setShowEditModal(true)}
                >
                  <img src={editDetailsIMG} alt="" />
                  Edit
                </button>
              </div>

              <p className="qp-sb-title">Traveller Details</p>

              <div className="qp-sb-rows">
                {/* Travellers */}
                <div className="qp-sb-row">
                  <div className="qp-sb-icon">
                    <svg
                      fill="none"
                      stroke="white"
                      strokeWidth="1.6"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="7" r="4" />
                      <path d="M5.5 20a7 7 0 0113 0" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="qp-sb-row-content">
                    <span className="qp-sb-row-label">Travellers</span>
                    <span className="qp-sb-row-val">
                      {travelerCount} Traveller{travelerCount !== 1 ? "s" : ""}
                      {Travelers?.length ? " - " : ""}
                      {Travelers?.map((r, i) => (
                        <span key={i}>
                          {getRelationName(r.relation)} · ({r.age}Y)
                          {i < Travelers.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>

                {/* Mobile */}
                <div className="qp-sb-row">
                  <div className="qp-sb-icon">
                    <svg
                      fill="none"
                      stroke="white"
                      strokeWidth="1.6"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="qp-sb-row-content">
                    <span className="qp-sb-row-label">Mobile</span>
                    <span className="qp-sb-row-val">+91 {MobileNo}</span>
                  </div>
                </div>

                {/* Destination */}
                <div className="qp-sb-row">
                  <div className="qp-sb-icon">
                    <svg
                      fill="none"
                      stroke="white"
                      strokeWidth="1.6"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                        strokeLinecap="round"
                      />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                  </div>
                  <div className="qp-sb-row-content">
                    <span className="qp-sb-row-label">Destination</span>
                    <span className="qp-sb-row-val">
                      {DestinationList?.map((place, i) => (
                        <span key={`${place.CountryName}-${i}`}>
                          {place.CountryName}
                          {i + 1 < DestinationList.length ? ", " : ""}
                        </span>
                      )) || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="qp-sb-divider" />

              <div className="qp-sb-dates">
                <div className="qp-sb-date-block">
                  <div className="qp-sb-date-label">Departure</div>
                  <div className="qp-sb-date-val">
                    {formatSafeDate(StartDate) || "—"}
                  </div>
                </div>
                <span className="qp-sb-arrow">→</span>
                <div className="qp-sb-date-block">
                  <div className="qp-sb-date-label">Return</div>
                  <div className="qp-sb-date-val">
                    {formatSafeDate(EndDate) || "—"}
                  </div>
                </div>
                {numberOfDays && (
                  <span className="qp-sb-days-pill">{numberOfDays}d</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Details Modal ── */}
      {showEditModal && (
        <Modal show={showEditModal} className="CustomeModal">
          <EditDetailForm
            closeModal={(data) => setShowEditModal(data)}
            updateDetails={updateDetails}
          />
        </Modal>
      )}

      {/* ── Sum Assured Modal ── */}
      <Modal
        show={showSumAssuredModal}
        onHide={() => setShowSumAssuredModal(false)}
        centered
        className="CustomeModal qp-filter-modal"
      >
        <div className="qp-fmodal-shell">
          <div className="qp-fmodal-head">
            <div>
              {/* <div className="qp-fmodal-kicker">Filter</div> */}
              <h4 className="qp-fmodal-title">Select Sum Assured</h4>
            </div>
            <button
              type="button"
              className="qp-fmodal-close"
              onClick={() => setShowSumAssuredModal(false)}
            >
              &#x2715;
            </button>
          </div>

          <div className="qp-fmodal-body">
            {sumAssuredOptions.map((item) => (
              <button
                key={item.PlanSuminsuredID}
                type="button"
                className={`qp-fopt${
                  selectedSumAssuredObj?.PlanSuminsuredID ===
                  item.PlanSuminsuredID
                    ? " active"
                    : ""
                }`}
                onClick={() => applySumAssured(item)}
              >
                <div className="qp-fopt-text">
                  <span className="qp-fopt-title">{item.Level}</span>
                  <span className="qp-fopt-sub">
                    {item.MinValue || item.MaxValue
                      ? `${CommaSeprator(item.MinValue)} - ${CommaSeprator(item.MaxValue)}`
                      : "Choose plan"}
                  </span>
                </div>
                <div className="qp-fopt-radio">
                  <div className="qp-fopt-radio-dot" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* â”€â”€ Insurer Modal â”€â”€ */}
      <Modal
        show={showInsurerModal}
        onHide={() => setShowInsurerModal(false)}
        centered
        className="CustomeModal qp-filter-modal"
      >
        <div className="qp-fmodal-shell">
          <div className="qp-fmodal-head">
            <div>
              <h4 className="qp-fmodal-title">Select Insurer</h4>
            </div>
            <button
              type="button"
              className="qp-fmodal-close"
              onClick={() => setShowInsurerModal(false)}
            >
              &#x2715;
            </button>
          </div>

          <div className="qp-fmodal-body">
            <button
              type="button"
              className={`qp-fopt qp-fopt--ins${
                draftInsurerIds.size === 0 ? " active" : ""
              }`}
              onClick={() => toggleDraftInsurer(null)}
            >
              <div className="qp-fopt-ins-row">
                <div className="qp-ins-avatar qp-ins-avatar--all">ALL</div>
                <div className="qp-fopt-text">
                  <span className="qp-fopt-title">All Insurers</span>
                  <span className="qp-fopt-sub">Show all available plans</span>
                </div>
              </div>
              <div className="qp-fopt-chk">
                <svg
                  viewBox="0 0 10 10"
                  fill="none"
                  width="10"
                  height="10"
                  stroke="white"
                  strokeWidth="2"
                >
                  <path d="M2 5l2.5 2.5L8 3" />
                </svg>
              </div>
            </button>

            {insurerList.map((ins) => (
              <button
                key={ins.InsurerID}
                type="button"
                className={`qp-fopt qp-fopt--ins${
                  draftInsurerIds.has(Number(ins.InsurerID)) ? " active" : ""
                }`}
                onClick={() => toggleDraftInsurer(Number(ins.InsurerID))}
              >
                <div className="qp-fopt-ins-row">
                  <div className="qp-ins-avatar qp-ins-avatar--logo">
                    {ins.COMPANYLOGO ? (
                      <img
                        src={ins.COMPANYLOGO}
                        alt={ins.InsurerName || ins.ShortName || "Insurer"}
                        className="qp-ins-logo"
                        loading="lazy"
                      />
                    ) : (
                      <span className="qp-ins-avatar-text">
                        {(ins.ShortName || ins.InsurerName || "IN")
                          .split(" ")
                          .map((part) => part[0])
                          .slice(0, 2)
                          .join("")}
                      </span>
                    )}
                  </div>
                  <div className="qp-fopt-text">
                    <span className="qp-fopt-title">
                      {ins.InsurerName || ins.ShortName || "Insurer"}
                    </span>
                    <span className="qp-fopt-sub">
                      {ins.planCount
                        ? `${ins.planCount} plan${ins.planCount !== 1 ? "s" : ""} available`
                        : "Tap to filter plans"}
                    </span>
                  </div>
                </div>
                <div className="qp-fopt-chk">
                  <svg
                    viewBox="0 0 10 10"
                    fill="none"
                    width="10"
                    height="10"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <path d="M2 5l2.5 2.5L8 3" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          <div className="qp-fmodal-footer">
            <button
              type="button"
              className="qp-fmodal-apply"
              onClick={applyInsurerFilter}
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuotationPage;
